import type { FunctionReturnType } from 'convex/server';
import { api } from 'convex/_generated/api';
import { toISODate } from '@shared/dates';
import { text as sharedText } from '@shared/text';
import type { Garden } from '@modules/my-gardens/business_logic';

export type Harvest = FunctionReturnType<typeof api.harvest.queries.getHarvestsForGarden>[number];

export type Plant = FunctionReturnType<typeof api.plants.queries.getAllPlants>[number];

export type MeasureType = 'quantity' | 'weight';
export type WeightUnit = 'g' | 'kg';
/** UI control: count, or weight in g/kg */
export type MeasureUnit = 'count' | 'g' | 'kg';

export function measureUnitToFields(unit: MeasureUnit): {
  measureType: MeasureType;
  weightUnit?: WeightUnit;
} {
  if (unit === 'count') {
    return { measureType: 'quantity' };
  }
  return { measureType: 'weight', weightUnit: unit };
}

export function fieldsToMeasureUnit(
  measureType: MeasureType,
  weightUnit?: WeightUnit
): MeasureUnit {
  if (measureType === 'quantity') {
    return 'count';
  }
  return weightUnit ?? 'g';
}

export interface HarvestDraft {
  gardenId: string;
  plantId: string;
  harvestDate: string;
  measureType: MeasureType;
  amount: string;
  weightUnit: WeightUnit;
}

export interface HarvestDayEntry {
  harvestId: string;
  plantId: string;
  plantName: string;
  plantEmoji: string;
  measureType: MeasureType;
  amountLabel: string;
}

export interface HarvestDay {
  date: string;
  dayName: string;
  entries: HarvestDayEntry[];
}

export function getUniquePlantIds(garden: Garden): string[] {
  return [...new Set(garden.plants.map((p) => p.plantId))];
}

export function getHarvestPlantsForGarden(garden: Garden | null, allPlants: Plant[]): Plant[] {
  if (!garden) return [];
  const uniqueIds = getUniquePlantIds(garden);
  return uniqueIds
    .map((id) => allPlants.find((p) => p.id === id))
    .filter((p): p is Plant => p !== undefined);
}

export function buildLast7DayDateRange(referenceDate: Date = new Date()): string[] {
  const dates: string[] = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(referenceDate);
    date.setDate(referenceDate.getDate() - i);
    dates.push(toISODate(date));
  }
  return dates;
}

export function getLast7DayBounds(dateRange: string[]): { startDate: string; endDate: string } {
  const sorted = [...dateRange].sort();
  return {
    startDate: sorted[0]!,
    endDate: sorted[sorted.length - 1]!,
  };
}

function getDayName(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return sharedText.daysOfWeek[date.getDay()]!;
}

export function formatHarvestAmount(
  measureType: MeasureType,
  amount: number,
  weightUnit?: WeightUnit
): string {
  if (measureType === 'quantity') {
    return String(amount);
  }
  return `${amount} ${weightUnit ?? 'g'}`;
}

export function buildHarvestDaySchedule(
  harvests: Harvest[],
  plants: Plant[],
  dateRange: string[]
): HarvestDay[] {
  const plantsById = new Map(plants.map((p) => [p.id, p]));

  return dateRange.map((date) => {
    const dayHarvests = harvests.filter((h) => h.harvestDate === date);

    const entries: HarvestDayEntry[] = dayHarvests.map((h) => {
      const plant = plantsById.get(h.plantId);
      return {
        harvestId: String(h._id),
        plantId: h.plantId,
        plantName: plant?.name ?? h.plantId,
        plantEmoji: plant?.emoji ?? '🌱',
        measureType: h.measureType,
        amountLabel: formatHarvestAmount(h.measureType, h.amount, h.weightUnit),
      };
    });

    return {
      date,
      dayName: getDayName(date),
      entries,
    };
  });
}

export type HarvestValidationError =
  | 'missing_garden'
  | 'missing_plant'
  | 'missing_date'
  | 'invalid_amount'
  | 'missing_weight_unit';

export function validateHarvestDraft(draft: HarvestDraft): HarvestValidationError | null {
  if (!draft.gardenId) return 'missing_garden';
  if (!draft.plantId) return 'missing_plant';
  if (!draft.harvestDate) return 'missing_date';

  const amount = Number(draft.amount);
  if (!Number.isFinite(amount) || amount <= 0) return 'invalid_amount';

  if (draft.measureType === 'weight' && !draft.weightUnit) {
    return 'missing_weight_unit';
  }

  return null;
}

export function parseHarvestAmount(draft: HarvestDraft): number {
  return Number(draft.amount);
}

export function isPlantInGarden(garden: Garden | null, plantId: string): boolean {
  if (!garden || !plantId) return false;
  return garden.plants.some((p) => p.plantId === plantId);
}
